import React from 'react';
import { Plane, Train, Bus, Car, Bike, Ship, Leaf } from 'lucide-react';

const MODE_META = {
  flight:    { label: 'Flight',    Icon: Plane, color: 'bg-sky-400'     },
  rail:      { label: 'Rail',      Icon: Train, color: 'bg-emerald-400' },
  bus:       { label: 'Bus',       Icon: Bus,   color: 'bg-amber-400'   },
  car:       { label: 'Car',       Icon: Car,   color: 'bg-red-400'     },
  taxi:      { label: 'Taxi',      Icon: Car,   color: 'bg-orange-400'  },
  motorbike: { label: 'Motorbike', Icon: Bike,  color: 'bg-fuchsia-400' },
  ferry:     { label: 'Ferry',     Icon: Ship,  color: 'bg-blue-400'    },
};

export default function ComparisonCard({ data }) {
  const comparisons = Array.isArray(data?.comparisons) ? data.comparisons : [];
  if (comparisons.length === 0) {
    return (
      <div className="mt-2 rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-500">
        No comparison available for {data?.origin || 'origin'} → {data?.destination || 'destination'}.
      </div>
    );
  }

  const max = Math.max(...comparisons.map((c) => c.co2e_kg || 0), 1);
  const greenest = comparisons[0];
  const worst = comparisons[comparisons.length - 1];
  const savings = worst && greenest ? Math.round(worst.co2e_kg - greenest.co2e_kg) : 0;

  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-white p-3 text-sm w-full">
      <div className="flex items-center gap-2 text-slate-700 font-medium mb-2">
        <Leaf className="w-4 h-4 text-emerald-600" />
        <span className="truncate">
          {data.origin} → {data.destination}
        </span>
      </div>

      <ul className="space-y-1.5">
        {comparisons.map((c) => {
          const meta = MODE_META[c.mode] || { label: c.mode, Icon: Car, color: 'bg-slate-400' };
          const pct = Math.max(3, Math.round((c.co2e_kg / max) * 100));
          const isBest = c.mode === greenest.mode;
          return (
            <li key={c.mode} className="flex items-center gap-2">
              <meta.Icon className={`w-3.5 h-3.5 flex-shrink-0 ${isBest ? 'text-emerald-600' : 'text-slate-500'}`} />
              <span className={`w-20 flex-shrink-0 text-xs ${isBest ? 'text-emerald-800 font-semibold' : 'text-slate-700'}`}>
                {meta.label}
              </span>
              <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden">
                <div
                  className={`h-full ${isBest ? 'bg-emerald-500' : meta.color}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className={`w-20 text-right text-xs font-mono ${isBest ? 'text-emerald-800 font-semibold' : 'text-slate-600'}`}>
                {Math.round(c.co2e_kg)} kg
              </span>
            </li>
          );
        })}
      </ul>

      {savings > 0 && (
        <div className="mt-2.5 pt-2 border-t border-slate-100 text-xs text-emerald-700">
          <span className="font-medium">{MODE_META[greenest.mode]?.label || greenest.mode}</span>
          {' '}saves ~{savings} kg vs {MODE_META[worst.mode]?.label || worst.mode}
          {greenest.distance_km ? ` · ~${Math.round(greenest.distance_km)} km` : ''}
        </div>
      )}
    </div>
  );
}
